import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MediaItem, SlideshowSettings } from '../types';
import { calculateOptimalDimensions, getViewportConstraints } from '../utils/mediaSizing';
import { useMediaCache } from '../hooks/useMediaCache';
import MediaSkeleton from './MediaSkeleton';

interface MediaPlayerProps {
  media: MediaItem[];
  settings: SlideshowSettings;
  isPlaying: boolean;
  currentIndex: number;
  onIndexChange: (index: number) => void;
  onVideoEnd?: () => void;
}

const MediaPlayer: React.FC<MediaPlayerProps> = ({
  media,
  settings,
  isPlaying,
  currentIndex,
  onIndexChange,
  onVideoEnd
}) => {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const currentMedia = media[currentIndex];

  // Use the media cache hook
  const {
    preloadAdjacentMedia,
    isMediaLoaded,
    isMediaLoading,
    preloadAllMedia,
    getLoadingProgress
  } = useMediaCache(media);

  // Calculate optimal dimensions for current media
  const [containerDimensions, setContainerDimensions] = useState<ReturnType<typeof calculateOptimalDimensions> | null>(null);

  // Log current displayed media object
  useEffect(() => {
    if (currentMedia) {
      console.log('Current displayed media object:', {
        index: currentIndex,
        totalMedia: media.length,
        media: currentMedia
      });
    }
  }, [currentMedia, currentIndex, media.length]);

  // Update dimensions when media changes or window resizes
  useEffect(() => {
    if (currentMedia) {
      const dimensions = calculateOptimalDimensions(currentMedia, getViewportConstraints());
      setContainerDimensions(dimensions);
      
      // Preload adjacent media when current media changes
      preloadAdjacentMedia(currentIndex);
    }
  }, [currentMedia, currentIndex, preloadAdjacentMedia]);

  // Recalculate dimensions when fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (currentMedia) {
        const dimensions = calculateOptimalDimensions(currentMedia, getViewportConstraints());
        setContainerDimensions(dimensions);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('resize', handleFullscreenChange);
    
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('resize', handleFullscreenChange);
    };
  }, [currentMedia]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (currentMedia) {
        const dimensions = calculateOptimalDimensions(currentMedia, getViewportConstraints());
        setContainerDimensions(dimensions);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentMedia]);

  // Preload all media on component mount
  useEffect(() => {
    if (media.length > 0) {
      preloadAllMedia();
    }
  }, [media, preloadAllMedia]);

  // Check if current media is loaded
  const isCurrentMediaLoaded = currentMedia ? isMediaLoaded(currentMedia.id) : false;
  const isCurrentMediaLoading = currentMedia ? isMediaLoading(currentMedia.id) : false;
  const loadingProgress = getLoadingProgress();

  const nextMedia = useCallback(() => {
    setIsTransitioning(true);
    setTimeout(() => {
      const nextIndex = (currentIndex + 1) % media.length;
      onIndexChange(nextIndex);
      setIsTransitioning(false);
    }, 300); // Transition duration
  }, [currentIndex, media.length, onIndexChange]);

  useEffect(() => {
    if (isPlaying && currentMedia) {
      if (currentMedia.type === 'image') {
        // For images, set a timer based on settings
        intervalRef.current = setTimeout(() => {
          nextMedia();
        }, settings.imageDuration * 1000);
      }
      // For videos, let the separate video autoplay effect handle it
    } else {
      // Clear any existing timer
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
      // Pause video if not playing
      if (videoRef.current && !isPlaying) {
        videoRef.current.pause();
      }
    }

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, [isPlaying, currentIndex, settings.imageDuration, currentMedia, nextMedia]);

  // Auto-play videos when they become current media (when slideshow is playing OR when user manually navigates)
  useEffect(() => {
    if (currentMedia && currentMedia.type === 'video' && isCurrentMediaLoaded) {
      console.log('Attempting to autoplay video:', currentMedia.id, 'isPlaying:', isPlaying, 'isLoaded:', isCurrentMediaLoaded);
      
      // Wait for video element to be available and visible
      const playVideo = async () => {
        try {
          if (videoRef.current) {
            // Try to play the video
            await videoRef.current.play();
            console.log('Video autoplay successful:', currentMedia.id);
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'NotAllowedError') {
            console.log('Video autoplay blocked by browser policy. User interaction required.');
            // Don't show error for autoplay policy - this is expected behavior
          } else {
            console.warn('Could not autoplay video:', error);
          }
        }
      };
      
      // Use a longer timeout to ensure the video element is properly loaded and visible
      const timeoutId = setTimeout(playVideo, 200);
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentMedia, currentIndex, isCurrentMediaLoaded, isPlaying]);

  const handleVideoEnd = useCallback(() => {
    if (onVideoEnd) {
      onVideoEnd();
    } else {
      nextMedia();
    }
  }, [onVideoEnd, nextMedia]);

  const getTransitionClass = () => {
    if (!isTransitioning) return '';
    
    switch (settings.transitionStyle) {
      case 'fade':
        return 'transition-fade';
      case 'dissolve':
        return 'transition-dissolve';
      case 'ease-in':
        return 'transition-ease-in';
      case 'slide':
        return 'transition-slide';
      default:
        return 'transition-fade';
    }
  };

  if (!currentMedia) {
    return (
      <div className="media-player">
        <div className="no-media">No media available</div>
      </div>
    );
  }

  return (
    <div 
      className={`media-player ${getTransitionClass()}`}
      style={containerDimensions ? {
        width: containerDimensions.width,
        height: containerDimensions.height,
        maxWidth: containerDimensions.maxWidth,
        maxHeight: containerDimensions.maxHeight,
      } : {}}
    >
      {currentMedia.type === 'image' ? (
        <>
          {/* Skeleton placeholder */}
          {!isCurrentMediaLoaded && (
            <MediaSkeleton 
              media={currentMedia}
              containerDimensions={containerDimensions || undefined}
            />
          )}
          
          {/* Actual image */}
          <img
            src={currentMedia.url}
            alt={`Media ${currentIndex + 1}`}
            className={`media-content ${isCurrentMediaLoaded ? 'loaded' : 'loading'} transition-${settings.transitionStyle}`}
            style={{ display: isCurrentMediaLoaded ? 'block' : 'none' }}
          />
        </>
      ) : (
        <>
          {/* Skeleton placeholder for video */}
          {!isCurrentMediaLoaded && (
            <MediaSkeleton 
              media={currentMedia}
              containerDimensions={containerDimensions || undefined}
            />
          )}
          
          {/* Actual video */}
          <video
            ref={videoRef}
            src={currentMedia.url}
            className={`media-content transition-${settings.transitionStyle}`}
            onEnded={handleVideoEnd}
            playsInline
            preload="auto"
            style={{ display: isCurrentMediaLoaded ? 'block' : 'none' }}
          />
          
          {/* Video play button overlay for autoplay-blocked videos */}
          {currentMedia.type === 'video' && isCurrentMediaLoaded && (
            <div 
              className="video-play-overlay"
              onClick={async () => {
                try {
                  if (videoRef.current) {
                    await videoRef.current.play();
                    console.log('Video started playing after user interaction');
                  }
                } catch (error) {
                  console.warn('Could not play video:', error);
                }
              }}
            >
              <div className="play-button">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
              <p>Click to play video</p>
            </div>
          )}
        </>
      )}
      
      {/* Loading indicator - only show for non-cached media that's actively loading */}
      {isCurrentMediaLoading && !isCurrentMediaLoaded && (
        <div className="loading-indicator">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Subtle preloading progress - only show during initial load */}
      {loadingProgress < 100 && loadingProgress > 0 && (
        <div className="preload-progress-mini">
          <div className="progress-dot"></div>
          <span className="progress-percentage">{Math.round(loadingProgress)}%</span>
        </div>
      )}
      
      {currentMedia.uploadedBy && (
        <div className="media-uploader">
          <span className="uploader-name">uploaded by - {currentMedia.uploadedBy}</span>
        </div>
      )}
      
      {/* Presented by text when header is hidden */}
      {settings.hideHeader && (
        <div className="media-presented-by">
          <span className="presented-by-text">presented by FiestAlbum</span>
        </div>
      )}
    </div>
  );
};

export default MediaPlayer;
